import { Controller } from "@hotwired/stimulus";
import L from "leaflet";
import escape from "lodash/escape";

import "leaflet.awesome-markers";
import "leaflet.smooth_marker_bouncing";
import "leaflet-maskcanvas";

export default class extends Controller {
  static targets = [
    "canvas",
    "propertyTemplate",
    "valuerTemplate",
  ];

  static values = {
    center: Array,
    markers: Array,
    bounceOnAdd: Boolean,
    maskPoints: Array
  };

  connect() {
    const ukBounds = L.latLngBounds([49.5, -8.7], [59.5, 1.9]);

    this.map = L.map(this.canvasTarget, {
      zoomControl: false,
      attributionControl: true
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(this.map);

    L.control.zoom({
      position: "topright",
      zoomInText: `
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M10.75 5.75H5.75M5.75 5.75H0.75M5.75 5.75V0.75M5.75 5.75V10.75" stroke="#1F040F" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`,
      zoomOutText: `
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="2" viewBox="0 0 12 2" fill="none">
        <path d="M0 0.625C0 0.279822 0.279822 0 0.625 0H11.0417C11.3868 0 11.6667 0.279822 11.6667 0.625C11.6667 0.970178 11.3868 1.25 11.0417 1.25H0.625C0.279822 1.25 0 0.970178 0 0.625Z" fill="#1F040F"/>
      </svg>`
    }).addTo(this.map);

    delete L.Icon.Default.prototype._getIconUrl;

    this._renderMarkers();
    this._renderMask();

    if (!this._fitToMarkers()) {
      if (this.centerValue?.length === 2) {
        this.map.setView(this.centerValue, 12);
      } else {
        this.map.fitBounds(ukBounds, { padding: [20, 20] });
      }
    }

    this._initialCenter = this.map.getCenter();
    this._initialZoom = this.map.getZoom();

    this._onFocusValuer = (e) => this._focusValuer(e.detail, { zoomOnClick: true });
    window.addEventListener("quotesMap:focusValuer", this._onFocusValuer);

    this._onResetView = () => this.resetView();
    window.addEventListener("quotesMap:resetView", this._onResetView);

    this._onMapClick = () => this.resetView();
    this.map.on("click", this._onMapClick);

    this._onDocumentClick = (e) => this._resetOnOutsideClick(e);
    document.addEventListener("click", this._onDocumentClick);

    this._onHoverValuerEnter = (e) => this._startMarkerAnimation(e.detail);
    window.addEventListener("quotesMap:hoverValuerEnter", this._onHoverValuerEnter);

    this._onHoverValuerLeave = (e) => this._stopMarkerAnimation(e.detail);
    window.addEventListener("quotesMap:hoverValuerLeave", this._onHoverValuerLeave);

    this._onViewportChange = () => this._emitVisibleValuers();
    this.map.on("moveend zoomend", this._onViewportChange);
    this._emitVisibleValuers();

    this._invalidateSizeTimeout = setTimeout(() => {
      if (this.map) this.map.invalidateSize();
    }, 0);
  }

  disconnect() {
    if (this._invalidateSizeTimeout) {
      clearTimeout(this._invalidateSizeTimeout);
      this._invalidateSizeTimeout = null;
    }

    if (this.map && this._onViewportChange) this.map.off("moveend zoomend", this._onViewportChange);
    if (this.map && this._onMapClick) this.map.off("click", this._onMapClick);

    document.removeEventListener("click", this._onDocumentClick);

    window.removeEventListener("quotesMap:focusValuer", this._onFocusValuer);
    window.removeEventListener("quotesMap:resetView", this._onResetView);
    window.removeEventListener("quotesMap:hoverValuerEnter", this._onHoverValuerEnter);
    window.removeEventListener("quotesMap:hoverValuerLeave", this._onHoverValuerLeave);

    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  _fitToMarkers() {
    if (!this.markerLayer) return false;

    const latlngs = [];
    this.markerLayer.eachLayer((layer) => {
      if (typeof layer.getLatLng === "function") latlngs.push(layer.getLatLng());
    });

    if (latlngs.length === 0) return false;
    if (latlngs.length === 1) {
      this.map.setView(latlngs[0], 13);
      return true;
    }

    this.map.fitBounds(L.latLngBounds(latlngs), { padding: [48, 48], maxZoom: 15 });
    return true;
  }

  _renderMarkers() {
    const items = this.markersValue || [];
    if (!items.length) return;

    if (this.markerLayer) this.markerLayer.remove();
    this.markerLayer = L.layerGroup().addTo(this.map);

    this.markerIndex = new Map();

    items.forEach((m) => {
      const latlng = L.latLng(m.lat, m.lng);
      const customIcon = this._customIconFor(m);
      const icon = customIcon || this._awesomeIconFor(m);

      const marker = L.marker(latlng, { icon });
      marker._meta = m;

      if ((m.kind || "").toLowerCase() === "property" && m.title) {
        const subtitle = m.subtitle
          ? `<div class="text-xs text-slate-600">${escape(m.subtitle)}</div>`
          : "";
        marker.bindTooltip(
          `<div class="leaflet-tooltip-content"><div class="font-semibold">${escape(m.title)}</div>${subtitle}</div>`,
          { direction: "top", offset: [0, -22], className: "leaflet-tooltip-custom", opacity: 1 }
        );
      } else if (!customIcon) {
        const title = m.title || "Marker";
        const subtitle = m.subtitle
          ? `<div class="text-xs text-slate-600">${escape(m.subtitle)}</div>`
          : "";
        marker.bindPopup(
          `<div class="font-semibold">${escape(title)}</div>${subtitle}`,
          { maxWidth: 240 }
        );
      }

      const key = (m.id != null ? String(m.id) : (m.title || ""));
      if (key) this.markerIndex.set(key, marker);

      if ((m.kind || "").toLowerCase() === "valuers") {
        marker.on("click", () => {
          this._emit("quotesMap:selectValuer", m);
        });
      }

      marker.addTo(this.markerLayer);

      if (this.bounceOnAddValue !== false && typeof marker.bounce === "function") {
        marker.bounce({ duration: 600, height: 60 });
      }
    });
  }

  _renderMask() {
    const pts = this.maskPointsValue || [];
    if (!pts.length || typeof L.maskCanvas !== "function") return;

    if (this.maskLayer) this.maskLayer.remove();

    this.maskLayer = L.maskCanvas({
      radius: 35,
      useAbsoluteRadius: true,
      color: "#000",
      opacity: 0.35,
      noMask: false,
      lineColor: "#000"
    });

    this.maskLayer.setData(pts);
    this.maskLayer.addTo(this.map);
  }

  _customIconFor(m) {
    const rawKind = (m.kind || "").toString().toLowerCase();
    let kind = rawKind;
    if (rawKind === "appraiser" || rawKind === "appraisers") kind = "valuers";
    if (rawKind === "valuer") kind = "valuers";

    if (kind === "property" && this.hasPropertyTemplateTarget) {
      const tmplEl = this.propertyTemplateTarget.content.firstElementChild?.cloneNode(true);
      if (tmplEl) {
        const html = `
          <div style="width:44px; height:44px; display:flex; align-items:center; justify-content:center;">
            <div style="width:44px; height:44px; transform-origin: center bottom;">
              ${tmplEl.outerHTML}
            </div>
          </div>`;
        return L.divIcon({
          html,
          className: "custom-pin",
          iconSize: [44, 44],
          iconAnchor: [22, 44],
          popupAnchor: [0, -36],
        });
      }
    }

    if (kind === "valuers" && this.hasValuerTemplateTarget) {
      const tmplEl = this.valuerTemplateTarget.content.firstElementChild?.cloneNode(true);
      if (tmplEl) {
        const titleEl = tmplEl.querySelector("[data-valuer-field='title']");
        const dividerEl = tmplEl.querySelector("[data-valuer-field='divider']");
        const feeEl = tmplEl.querySelector("[data-valuer-field='fee']");

        if (titleEl) titleEl.textContent = m.title || "";

        const fee = m.subtitle || "";
        if (fee) {
          if (feeEl) feeEl.textContent = fee;
        } else {
          dividerEl?.remove();
          feeEl?.remove();
        }

        return L.divIcon({
          html: tmplEl.outerHTML,
          className: "",
          iconSize: [0, 0],
          iconAnchor: [0, 0],
          popupAnchor: [0, -8],
        });
      }
    }

    return null;
  }

  _awesomeIconFor(m) {
    const color = m.color || "blue";
    const icon = m.icon || "map-marker";

    return L.AwesomeMarkers.icon({
      prefix: "fa",
      icon,
      markerColor: color,
      extraClasses: "shadow-md"
    });
  }

  _emitVisibleValuers() {
    if (!this.map || !this.markerLayer) return;
    const b = this.map.getBounds();
    const visible = [];
    this.markerLayer.eachLayer((layer) => {
      const m = layer?._meta;
      if (m?.kind === "valuers" && b.contains(layer.getLatLng())) {
        visible.push(m);
      }
    });
    this._emit("quotesMap:visibleValuers", { valuers: visible });
  }

  _emit(name, detail) {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  }

  resetView() {
    if (!this.map || !this._initialCenter) return;

    this.map.flyTo(this._initialCenter, this._initialZoom, { duration: 0.6 });
  }

  _resetOnOutsideClick(event) {
    if (this.element.contains(event.target)) return;
    if (event.target.closest('[data-controller~="quote-map-card"]')) return;

    this.resetView();
  }

  _focusValuer(item, { zoomOnClick } = { zoomOnClick: false }) {
    if (!item) return;
    const key = (item.id != null ? String(item.id) : (item.title || ""));
    const marker = this.markerIndex?.get(key);
    const tgt = marker?.getLatLng() || (item.lat != null && item.lng != null ? L.latLng(item.lat, item.lng) : null);
    if (!tgt) return;

    if (zoomOnClick) {
      const current = this.map.getZoom();
      const desired = Math.max(current + 3, 14);
      const capped = Math.min(desired, 17);
      this.map.flyTo(tgt, capped, { duration: 0.8 });
    } else {
      this.map.panTo(tgt, { animate: true, duration: 0.4 });
    }

    if (marker) {
      this._addMarkerBounceAnimation(marker);
      if (marker.getPopup?.()) marker.openPopup();
    }
  }

  _startMarkerAnimation(item) {
    if (!item) return;
    const key = (item.id != null ? String(item.id) : (item.title || ""));
    const marker = this.markerIndex?.get(key);

    if (marker && marker._icon) {
      const inner = marker._icon.querySelector("div");
      const targetEl = inner || marker._icon;
      targetEl.classList.add("animate-valuer-marker-bounce-continuous");
    }
  }

  _stopMarkerAnimation(item) {
    if (!item) return;
    const key = (item.id != null ? String(item.id) : (item.title || ""));
    const marker = this.markerIndex?.get(key);

    if (marker && marker._icon) {
      const inner = marker._icon.querySelector("div");
      const targetEl = inner || marker._icon;
      targetEl.classList.remove("animate-valuer-marker-bounce-continuous");
    }
  }

  _addMarkerBounceAnimation(marker) {
    if (!marker._icon) return;

    const inner = marker._icon.querySelector("div");
    const targetEl = inner || marker._icon;

    targetEl.classList.add("animate-valuer-marker-bounce");
    setTimeout(() => {
      targetEl.classList.remove("animate-valuer-marker-bounce");
    }, 800);
  }
}
